import { DropdownWithStatistic } from "components";
import { useTranslation } from "react-i18next";
import { Loader, Stat } from "ui";

import { OtherCostsResponse } from "../../../apis";
import { SectionCard } from "../atoms";
import { formatPercent, formatXof, shareOf } from "../financialFormat";

import styles from "./OtherCostsSection.module.scss";

interface OtherCostsSectionProps {
  data: OtherCostsResponse | undefined;
  periodRevenue: number | undefined;
}

const OtherCostsSection = ({ data, periodRevenue }: OtherCostsSectionProps) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <SectionCard title="Other costs">
        <Loader />
      </SectionCard>
    );
  }

  const share = periodRevenue
    ? shareOf(data.total.amount, periodRevenue)
    : null;

  return (
    <SectionCard title="Other costs">
      <div className={styles.topBlock}>
        <Stat
          size="lg"
          title="Other costs for the period"
          amount={formatXof(data.total.amount)}
        />
        <div className={styles.shareNote}>
          {formatPercent(share, 1)} {t("of period revenue")} ·{" "}
          {t("excluding cost of goods and advertising")}
        </div>
      </div>

      <DropdownWithStatistic label="Other costs breakdown" defaultOpen>
        <div className={styles.grid}>
          <div className={styles.card}>
            <Stat
              size="sm"
              title="Mobile money commissions"
              amount={formatXof(data.mobileMoneyCommissions.amount)}
            />
            <div className={styles.card__sub}>
              {t("{{rate}}% of {{amount}} collected", {
                rate: data.mobileMoneyCommissions.ratePercentage,
                amount:
                  data.mobileMoneyCommissions.collectedAmount.toLocaleString(
                    "en-US"
                  )
              })}
            </div>
          </div>

          <div className={styles.card}>
            <Stat
              size="sm"
              title="Returns and refunds"
              amount={formatXof(data.returnedDamagedItems.amount)}
            />
            <div className={styles.card__sub}>
              {data.returnedDamagedItems.itemCount} {t("items")}
            </div>
          </div>

          <div className={styles.card}>
            <Stat
              size="sm"
              title="Losses"
              amount={formatXof(data.lostItems.amount)}
            />
            <div className={styles.card__sub}>
              {data.lostItems.itemCount} {t("items")}
            </div>
          </div>

          <div className={styles.card}>
            <Stat
              size="sm"
              title="Damaged items"
              amount={formatXof(data.damagedItems.amount)}
            />
            <div className={styles.card__sub}>
              {data.damagedItems.itemCount} {t("items")}
            </div>
          </div>
        </div>
      </DropdownWithStatistic>
    </SectionCard>
  );
};

export default OtherCostsSection;
